// Customer-facing Uptick setup guide (mirrors UPTICK_SETUP_GUIDE.md in the
// repo — keep the two in step). Linked from the Integrations page.

import { Link } from "react-router-dom";

const REFS: Array<[string, string, string]> = [
  ["BT1851_RESULT", "Assertion", "Pass / Fail (a fail carries the test name and report link as the reason)"],
  ["BT1851_CAPACITY_AH", "Decimal", "delivered capacity, Ah"],
  ["BT1851_CAPACITY_PCT", "Integer", "delivered capacity, % of rated"],
  ["BT1851_RATED_AH", "Decimal", "rated capacity, Ah"],
  ["BT1851_END_VOLTAGE", "Decimal", "battery voltage at end, V"],
  ["BT1851_DURATION_MIN", "Integer", "test duration, minutes"],
  ["BT1851_REPORT_URL", "Text", "link to the cloud report"],
  ["BT1851_TEST_NAME", "Text", "test name"],
  ["BT1851_TEST_DATE", "Text", "test date"],
  ["BT1851_BATTERY", "Text", "battery maker and model"],
];

export function UptickGuidePage() {
  return (
    <section style={{ maxWidth: "60em" }}>
      <p><Link to="/integrations">← Integrations</Link></p>
      <h2>Uptick — setup guide</h2>
      <p>
        When a battery test finishes on a BT-1851 tester, its result is written
        onto the matching Uptick work order: Pass/Fail and the performed date
        on the service task, the readings as answers to your own inspection
        questions, and the PDF report as a document on the work order (in a{" "}
        <code>BT-1851</code> folder) and on the asset. Nothing from your Uptick
        tenant is copied or stored here; every lookup is live, and work orders
        are never closed by the integration.
      </p>

      <h3>Part 1 — in Uptick (an Uptick administrator, about 20 minutes)</h3>

      <h4>1. Create an API application</h4>
      <p>
        Control Panel → Integrations → Uptick API → Create Application. Copy
        the <strong>Client ID</strong> and the <strong>Client secret</strong>{" "}
        — the secret is shown once.
      </p>

      <h4>2. Create a security group for the integration</h4>
      <p>
        Control Panel → Security &amp; Access → Security groups → create
        &ldquo;BT-1851 Integration&rdquo; with only:
      </p>
      <table>
        <tbody>
          <tr><td>Tasks</td><td>Can see all tasks, Can update tasks</td></tr>
          <tr><td>Properties</td><td>View</td></tr>
          <tr><td>Clients</td><td>View</td></tr>
          <tr><td>Assets</td><td>View</td></tr>
          <tr><td>Documents</td><td>Upload documents</td></tr>
        </tbody>
      </table>
      <p className="muted">Nothing else — no delete, no billing, no quoting.</p>

      <h4>3. Create a service user</h4>
      <p>
        People → Users → create a dedicated user (for example{" "}
        <code>bt1851-integration@yourcompany.com</code>, a mailbox you
        control), licence type <strong>API</strong>, member of the
        &ldquo;BT-1851 Integration&rdquo; group, with a strong password. The
        API licence deliberately blocks web sign-in; that is expected. Never
        use a real person&rsquo;s login.
      </p>

      <h4>4. Create the prompt set that receives the readings</h4>
      <p>Control Panel → Work &amp; Scheduling → Prompts → Create.</p>
      <ol>
        <li>Name &ldquo;BT-1851 Battery Test&rdquo;, Ref <code>BT1851</code> → Save and Continue.</li>
        <li><strong>Standards</strong>: the standard your battery routines run under (for example AS1851-2012 (a1)).</li>
        <li><strong>Routine Service Types</strong>: the routine type(s) your battery service tasks are generated from.</li>
        <li><strong>Asset Types</strong>: your battery asset type(s).</li>
        <li>
          Add Question for each line you want filled. The <strong>Ref</strong>{" "}
          column is what the integration looks for; use these refs (any subset):
          <table style={{ marginTop: "0.5em" }}>
            <tbody>
              {REFS.map(([ref, type, what]) => (
                <tr key={ref}><td><code>{ref}</code></td><td className="muted">{type}</td><td>{what}</td></tr>
              ))}
            </tbody>
          </table>
        </li>
        <li>
          Expand the standard&rsquo;s column in the question grid and{" "}
          <strong>tick the frequency</strong> for every question (the routine
          frequency you test at, for example Annual or 2Y). Uptick refuses to
          save a standard-linked set without it, and a question without a tick
          never appears on a work order.
        </li>
        <li>Save Prompt Set.</li>
      </ol>
      <p className="muted">
        You can also add these questions to an existing set (for example the
        fire-panel annual section) instead of a dedicated one — the ref is all
        that matters.
      </p>

      <h4>5. Check the batteries are on work orders</h4>
      <p>
        Each battery set needs its own asset, of the chosen asset type, on
        the property and on a routine of the chosen routine service type.
        That is what makes a battery service task appear on a
        technician&rsquo;s work order for the integration to write to. Many
        tenants keep battery details on the panel asset instead &mdash;{" "}
        <Link to="/integrations/uptick-batteries">Batteries in Uptick</Link>{" "}
        explains the recommended setup and how to move existing details.
      </p>

      <h3>Part 2 — in BT-1851 Cloud (an organisation owner or admin)</h3>
      <ol>
        <li>
          <Link to="/integrations">Integrations</Link> → Uptick → Connect: your
          tenant subdomain (the <code>company</code> in{" "}
          <code>company.onuptick.com</code>), the Client ID and secret from
          step 1, and the service user&rsquo;s username and password from
          step 3. Credentials are stored encrypted and never shown again.
        </li>
        <li>
          <strong>Test connection.</strong> The Setup check should list your
          BT1851 questions, the prompt set they sit in, and the asset types
          wired to it. Press it again whenever you change the prompt set.
        </li>
        <li>
          <strong>Settings</strong> on the same card: where the PDF is attached
          (work order, asset, both — the default — or nothing) and{" "}
          <strong>Auto-push new results</strong> (on by default).
        </li>
      </ol>

      <h3>Part 3 — day to day</h3>
      <ul>
        <li>
          <strong>Manual push.</strong> Open a test → Uptick card → search the
          property → pick the battery asset → pick the work order → Send. The
          card then shows what was written.
        </li>
        <li>
          <strong>Auto-push.</strong> With the option on, a result that arrives
          from a tester carrying an Uptick work-order reference is sent
          immediately. Results without one wait under &ldquo;Awaiting an
          Uptick work order&rdquo; on the Integrations page; open one and pick
          the work order to send it.
        </li>
        <li>
          <strong>A battery line already ticked in Uptick.</strong> When the
          technician&rsquo;s tick matches the tester&rsquo;s verdict, the tick
          is kept and the readings and report are added automatically. When it
          disagrees, or the line was written by another BT-1851 test, the new
          result waits in the same list and the test&rsquo;s Uptick card shows
          both: choose <strong>Replace the result</strong>, or pick another
          work order.
        </li>
        <li>
          <strong>Re-send PDF</strong> on a sent test re-uploads the report
          (after changing the document setting, for example).
        </li>
        <li>
          A partial failure (result written, document not) shows a{" "}
          <strong>Retry upload</strong> button.
        </li>
      </ul>

      <h3>Part 4 — if something looks wrong</h3>
      <table>
        <tbody>
          <tr><td>Setup check says no BT1851 questions found</td><td>Refs must start with <code>BT1851_</code> and the question must be active. Press Test connection after editing.</td></tr>
          <tr><td>Questions are found but a push writes &ldquo;verdict only&rdquo;</td><td>The prompt set does not apply to that service task: check the set&rsquo;s Standard, the frequency ticks, and that it is attached to that asset type and routine service type.</td></tr>
          <tr><td>The asset picker shows no assets</td><td>The battery&rsquo;s asset type is not wired to the set — use &ldquo;Show all asset types&rdquo;, or attach the set to that type.</td></tr>
          <tr><td>The work-order step shows nothing</td><td>The battery asset has no service task on any work order yet. Add it to a task in Uptick first.</td></tr>
          <tr><td>Connection shows &ldquo;Insufficient&rdquo;</td><td>The service user cannot read tasks. Check its group and licence, then Test connection.</td></tr>
        </tbody>
      </table>
    </section>
  );
}
